<script setup lang="ts">
import type { TabsTriggerProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { computed, inject } from 'vue'
import { reactiveOmit } from '@vueuse/core'
import { TabsTrigger, useForwardProps } from 'reka-ui'
import { cn } from '@ds/lib/utils'
import type { TabsVariant } from '.'

const props = defineProps<TabsTriggerProps & { class?: HTMLAttributes['class'] }>()

const delegatedProps = reactiveOmit(props, 'class')

const forwardedProps = useForwardProps(delegatedProps)

// No `TabsList` ancestor (used standalone, or in a test) falls back to the
// same default `TabsList` itself defaults to.
const variant = inject<TabsVariant>('tabsVariant', 'underline')

/*
 * Canonical `Tabs.jsx` gives each variant its own padding, radius and
 * active-state treatment — `underline`'s active tab gets a brand bar under
 * it (`(derived)`: the mock's raw `background: var(--brand)` would be
 * `bg-brand`, but this token is exposed as `text-brand` only, calibrated for
 * text contrast and deliberately not usable as a fill — `bg-primary` is the
 * fill counterpart the token split itself points to), `pill`'s lifts onto a
 * card-colored, shadowed chip instead.
 */
const variantClass = computed(() =>
  variant === 'pill'
    ? [
      'py-1.75 px-3.5 rounded-ds-sm',
      'data-[state=active]:bg-card data-[state=active]:shadow-ds-xs data-[state=active]:text-foreground',
    ].join(' ')
    : [
      'py-2.5 px-1 mx-2',
      'data-[state=active]:text-brand',
      'data-[state=active]:after:content-[\'\'] data-[state=active]:after:absolute data-[state=active]:after:left-0 data-[state=active]:after:right-0 data-[state=active]:after:-bottom-px data-[state=active]:after:h-0.5 data-[state=active]:after:rounded-full data-[state=active]:after:bg-primary',
    ].join(' '),
)
</script>

<template>
  <TabsTrigger
    v-bind="forwardedProps"
    :class="cn(
      'relative inline-flex items-center justify-center whitespace-nowrap',
      'text-sm font-medium text-muted-foreground cursor-pointer',
      'transition-colors duration-ds-fast ease-ds-standard',
      'focus-visible:outline-none focus-visible:shadow-ds-focus',
      'disabled:pointer-events-none disabled:opacity-50',
      'data-[state=active]:font-bold',
      variantClass,
      props.class,
    )"
  >
    <span class="truncate">
      <slot />
    </span>
  </TabsTrigger>
</template>
